import { formGroup, formArray, formControl, FormDataType } from '@core/utils/form';
import { Proposal } from '../Blocks/types';
import { FontResponse } from '@business/entities/google';
import { defineBlockAdvanceForm } from '../Blocks/Common/AdvanceSettings';
import { definePricingBlockForm } from '../Blocks/PricingBlock';
import { definePackageForm, defineSubProjectsForm, defineListItemsForm } from '@modules/proposals/AddEditProposalPage/AddEditProposalForm';
import { defineProposalDiscountForm } from './Components/ProposalProductsOverlay';
import { defineTmInvoiceSalesTaxForm } from '@modules/common/TmInvoiceSalesTax';

// A reusable block title style (e.g. "Block Title 1"), saved on the template. Admins add/remove them in
// Settings → Block Title Formats; a section picks one by id, so editing a format restyles every section
// using it — and only that format.
export function defineBlockTitleFormatForm() {
  return formGroup({
    id: formControl<string>({ initialValue: '' }),
    name: formControl<string>({ label: 'Format Name', initialValue: 'Block Title' }),
    font: formControl<FontResponse | undefined>({ label: 'Font (Google Font)', placeholder: 'Proposal font' }),
    fontSize: formControl<number | undefined>({ label: 'Font Size (px)', initialValue: 24 }),
    fontWeight: formControl<string | undefined>({ label: 'Font Weight', initialValue: '600' }),
    color: formControl<string | undefined>({ label: 'Color', initialValue: '' }),
    italic: formControl<boolean>({ label: 'Italic', initialValue: false }),
    uppercase: formControl<boolean>({ label: 'Uppercase', initialValue: false }),
    position: formControl<'left' | 'center' | 'right'>({ label: 'Title Position', initialValue: 'left' }),
  });
}

const defaultTitleFormat = (id: string, name: string, fontSize: number, fontWeight: string) => ({
  id,
  name,
  font: undefined,
  fontSize,
  fontWeight,
  color: '',
  italic: false,
  uppercase: false,
  position: 'left' as const,
});

// The three agreed Phase 1 formats every template starts with (removable / renamable).
export const DEFAULT_BLOCK_TITLE_FORMATS = [
  defaultTitleFormat('title1', 'Block Title 1', 32, '700'),
  defaultTitleFormat('title2', 'Block Title 2', 26, '600'),
  defaultTitleFormat('title3', 'Block Title 3', 20, '600'),
];

export function defineProposalBlockForm() {
  return formGroup({
    id: formControl<string>({ initialValue: '0' }),
    title: formControl<string | undefined>({ label: 'Block Title (Optional)', placeholder: 'Block Title (Optional)' }),
    // Shown in the condensed (collapsed) section header on the client proposal view —
    // a short plain-language summary and a small image. Both optional.
    description: formControl<string | undefined>({
      label: 'Block Description (Optional)',
      placeholder: 'Short description shown when the section is collapsed',
    }),
    image: formControl<string | undefined>({ label: 'Block Image (Optional)' }),
    // Collapsed-header alignment: LEFT shows the description inline right after the title;
    // CENTER/RIGHT put the description below the title.
    titlePosition: formControl<'left' | 'center' | 'right'>({ label: 'Block Title Position', initialValue: 'left' }),
    // Proposal navigation grouping: blocks sharing a group name are listed under one top-level
    // heading in the navigation. The page content and block order are unchanged.
    navGroup: formControl<string | undefined>({ label: 'Navigation Group (Optional)', placeholder: 'e.g. About Us' }),
    // Parent section: groups other sections under it in the client-facing navigation menu (one level).
    isParentSection: formControl<boolean>({ label: 'Parent section (groups other sections in the client menu)', initialValue: false }),
    // Block id of the parent section this block is listed under in the client menu. Content/order unchanged.
    parentId: formControl<string | undefined>({ label: 'Show under parent section', placeholder: 'None (standalone)' }),
    // Leaves the block out of the client-facing navigation menu only — the block stays on the
    // proposal page and in the builder sidebar. Missing on older saved blocks = shown.
    hideInNav: formControl<boolean>({ label: 'Hide from client navigation menu', initialValue: false }),
    // Id of a saved title format (`blockTitleFormats` on the template). Empty = style the title by hand.
    titleFormat: formControl<string | undefined>({ label: 'Block Title Format', placeholder: 'None (style manually)' }),
    isSection: formControl<boolean>({ initialValue: false }),
    type: formControl<Proposal.BlockType>({ placeholder: 'Block Type' }),
    variation: formControl<string>({ placeholder: 'Block Variation' }),
    menuOrder: formControl<number>({ initialValue: 0 }),
    locked: formControl<boolean>({ initialValue: false }),
    data: formControl<any>({ placeholder: 'Block Data' }),
  });
}

export function defineProposalForm() {
  return formGroup({
    title: formControl<string>({ label: 'Title', initialValue: 'Proposal Template' }),
    font: formControl<FontResponse>({ label: 'Font (Google Font)', placeholder: 'Search Google Fonts...' }),
    primaryColor: formControl<string>({ label: 'Primary Color', initialValue: '#45A8F7' }),
    headingFontSize: formControl<number>({ label: 'Default Heading Font Size', initialValue: 35 }),
    fontSize: formControl<number>({ label: 'Default Paragraph Font Size', initialValue: 20 }),
    carouselSectionDropDownSize: formControl<string>({ label: 'Carousel Section Size', placeholder: 'Carousel Section Size' }),
    carouselSectionColor: formControl<string>({ label: 'Carousel Section Color', placeholder: 'Carousel Section Color' }),
    blocks: formArray(() => defineProposalBlockForm()),
    background: formControl<string>({ label: 'Background Color', initialValue: '' }),
    // estimate: defineEstimateForm(),
    // packages: formArray(() => definePackageForm()),
    subProjects: formArray(() => defineSubProjectsForm()),
    listItems: formArray(() => defineListItemsForm()),
    pricing: definePricingBlockForm(),
    productsAdvance: defineBlockAdvanceForm(),
    fontWeight: formControl<string>({ label: 'Default Font Weight' }),
    terms: formControl<string>({
      label: 'Terms and Conditions',
      initialValue: 'By signing I agree this is a legal representation of my signature for all purposes just the same as pen-and-paper signature.',
    }),
    discount: defineProposalDiscountForm(),
    salesTax: defineTmInvoiceSalesTaxForm(),
    productsHash: formControl<string>({ initialValue: '' }),
    // Reusable section title styles (Settings → Block Title Formats). Templates saved without the key get the
    // three defaults; an admin-emptied list stays empty.
    blockTitleFormats: formArray(() => defineBlockTitleFormatForm(), { initialValue: DEFAULT_BLOCK_TITLE_FORMATS }),
  });
}

export type ProposalForm = ReturnType<typeof defineProposalForm>;
export type ProposalBlockForm = ReturnType<typeof defineProposalBlockForm>;

export type ProposalFormData = FormDataType<typeof defineProposalForm>;
export type ProposalBlockFormData = FormDataType<typeof defineProposalBlockForm>;
export type BlockTitleFormatForm = ReturnType<typeof defineBlockTitleFormatForm>;
export type BlockTitleFormatData = FormDataType<typeof defineBlockTitleFormatForm>;
